/**
 * CardStrip — the OEM Home bottom-strip horizontal scroller (Phase 2).
 *
 * Ports the reference HomeOEM bottom strip: a horizontally-scrolling row of
 * fixed-size cards floating over the map. Card order matches the gripp-os
 * reference exactly: Activity · Assets · Routines · Products · Customers ·
 * Campaigns.
 *
 * Four cards are map-plottable (Activity / Assets / Routines / Products):
 * selecting one lifts its `HomeEntityType` to the page via `onSelectPlot`,
 * switching the map's plotting mode. Selecting the active card again clears it
 * (back to the resting ASSET view). Campaigns / Customers are display-only.
 */
import React from 'react';
import { HomeEntityType } from '@gripp/shared-logic';
import { ScrollView, StyleSheet, View } from 'react-native';
import { tokens } from '@gripp/shared-ui';
import { TimeRange } from '../types';
import { ActivityCard } from './activityCard';
import { AssetsCard } from './assetsCard';
import { RoutinesCard } from './routinesCard';
import { ProductsCard } from './productsCard';
import { CustomersCard } from './customersCard';
import { CampaignsCard } from './campaignsCard';

export interface CardStripProps {
  range: TimeRange;
  /** Active map plotting mode (null = resting ASSET view). */
  plotMode: HomeEntityType | null;
  /** Active sub-filter (chart item) within the plotting mode, or null. */
  statusFilter?: string | null;
  /** Toggles a plotting mode (selecting the active one again clears it). */
  onSelectPlot: (entityType: HomeEntityType) => void;
  /** Click a chart item: plot that entity and filter the map to `status`. */
  onSelectStatus?: (entityType: HomeEntityType, status: string) => void;
}

/** Fixed-footprint wrapper for one card (design: w-[360px] h-[270px] snap-start). */
const StripCard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <View style={styles.stripCard}>{children}</View>
);

export const CardStrip: React.FC<CardStripProps> = ({
  range,
  plotMode,
  statusFilter,
  onSelectPlot,
  onSelectStatus,
}) => {
  // The sub-filter only applies to whichever entity is currently plotted.
  const statusFor = (entity: HomeEntityType): string | null =>
    plotMode === entity ? statusFilter ?? null : null;
  return (
    <>
      {/* Web-only: thin, tokenized horizontal scrollbar so mouse users can reach
          the off-screen cards (gripp-os hides it and relies on trackpad scroll,
          which strands mouse users on a desktop surface). Thumb is the
          soft-text role token, composed to 50%/80% alpha with color-mix because
          a CSS variable cannot carry a hex-alpha suffix. */}
      <style>{`
        #home-card-scroller {
          scrollbar-width: thin;
          scrollbar-color: color-mix(in srgb, var(--text-soft) 50%, transparent) transparent;
        }
        #home-card-scroller::-webkit-scrollbar { height: 8px; }
        #home-card-scroller::-webkit-scrollbar-track { background: transparent; }
        #home-card-scroller::-webkit-scrollbar-thumb {
          background: color-mix(in srgb, var(--text-soft) 50%, transparent);
          border-radius: 4px;
        }
        #home-card-scroller::-webkit-scrollbar-thumb:hover {
          background: color-mix(in srgb, var(--text-soft) 80%, transparent);
        }
      `}</style>
      <ScrollView
        horizontal
        nativeID="home-card-scroller"
        showsHorizontalScrollIndicator
        contentContainerStyle={styles.content}
        style={styles.scroller}
        testID="home-dashboard-cardstrip"
      >
        <StripCard>
        <ActivityCard
          range={range}
          selected={plotMode === HomeEntityType.Activity}
          onToggleSelect={() => onSelectPlot(HomeEntityType.Activity)}
          activeStatus={statusFor(HomeEntityType.Activity)}
          onSelectStatus={(status) =>
            onSelectStatus?.(HomeEntityType.Activity, status)
          }
        />
      </StripCard>
      <StripCard>
        <AssetsCard
          range={range}
          selected={plotMode === HomeEntityType.Asset}
          onToggleSelect={() => onSelectPlot(HomeEntityType.Asset)}
          activeStatus={statusFor(HomeEntityType.Asset)}
          onSelectStatus={(status) =>
            onSelectStatus?.(HomeEntityType.Asset, status)
          }
        />
      </StripCard>
      <StripCard>
        <RoutinesCard
          range={range}
          selected={plotMode === HomeEntityType.Routine}
          onToggleSelect={() => onSelectPlot(HomeEntityType.Routine)}
          activeStatus={statusFor(HomeEntityType.Routine)}
          onSelectStatus={(status) =>
            onSelectStatus?.(HomeEntityType.Routine, status)
          }
        />
      </StripCard>
      <StripCard>
        <ProductsCard
          range={range}
          selected={plotMode === HomeEntityType.Product}
          onToggleSelect={() => onSelectPlot(HomeEntityType.Product)}
          activeStatus={statusFor(HomeEntityType.Product)}
          onSelectStatus={(status) =>
            onSelectStatus?.(HomeEntityType.Product, status)
          }
        />
      </StripCard>
      <StripCard>
        <CustomersCard range={range} />
      </StripCard>
      <StripCard>
        <CampaignsCard range={range} />
      </StripCard>
      </ScrollView>
    </>
  );
};

CardStrip.displayName = 'CardStrip';

const styles = StyleSheet.create({
  scroller: {
    // The scroller is overlaid on the map; let it size to its content height.
    flexGrow: 0,
  },
  content: {
    flexDirection: 'row',
    gap: tokens.spacing.md, // gap-3 (12)
    paddingHorizontal: tokens.spacing.lg,
    paddingBottom: tokens.spacing.lg,
    paddingTop: tokens.spacing.sm,
  },
  stripCard: {
    width: 360,
    height: 270,
  },
});
